<script setup lang="ts">
  import { computed, type Component } from 'vue'
  import {
    Checkbox,
    DateTimeInput,
    FormField,
    InputGroupInput,
    InputGroupNumber,
    InputGroupTextarea,
    SelectInput,
    type SelectInputItem
  } from '@gat-ship/vue-components'
  import type { ResolvedCustomField } from '../model/types'

  const props = defineProps<{
    field: ResolvedCustomField
    form: { Field: Component }
  }>()

  const requiredMessage = computed<string | false>(() =>
    props.field.required ? `${props.field.label} is required` : false
  )

  const selectItems = computed<SelectInputItem[]>(() => {
    if (!props.field.dropdownOptions) return []
    return props.field.dropdownOptions.map((opt) => ({
      title: opt,
      value: opt
    }))
  })
</script>

<template>
  <FormField
    v-if="field.inputType === 'text'"
    :form="form"
    :name="field.fieldName"
    :label="field.label"
    :required="requiredMessage"
    :disabled="field.disabled"
    :hint="field.hint ?? undefined"
  >
    <template #default="{ field: f }">
      <InputGroupInput
        :id="f.name"
        :model-value="f.state.value ?? ''"
        :disabled="field.disabled"
        @update:model-value="f.handleChange"
        @blur="f.handleBlur"
      />
    </template>
  </FormField>

  <FormField
    v-else-if="field.inputType === 'multiline'"
    :form="form"
    :name="field.fieldName"
    :label="field.label"
    :required="requiredMessage"
    :disabled="field.disabled"
    :hint="field.hint ?? undefined"
  >
    <template #default="{ field: f }">
      <InputGroupTextarea
        :id="f.name"
        :model-value="f.state.value ?? ''"
        :rows="3"
        :disabled="field.disabled"
        @update:model-value="f.handleChange"
        @blur="f.handleBlur"
      />
    </template>
  </FormField>

  <FormField
    v-else-if="field.inputType === 'number'"
    :form="form"
    :name="field.fieldName"
    :label="field.label"
    :required="requiredMessage"
    :disabled="field.disabled"
    :hint="field.hint ?? undefined"
  >
    <template #default="{ field: f }">
      <InputGroupNumber
        :id="f.name"
        :model-value="f.state.value"
        :number-of-decimals="field.numberOfDecimals ?? 0"
        :fixed-decimals="true"
        :disabled="field.disabled"
        @update:model-value="f.handleChange"
        @blur="f.handleBlur"
      />
    </template>
  </FormField>

  <FormField
    v-else-if="field.inputType === 'datetime'"
    :form="form"
    :name="field.fieldName"
    :label="field.label"
    :required="requiredMessage"
    :disabled="field.disabled"
    :hint="field.hint ?? undefined"
  >
    <template #default="{ field: f, invalid }">
      <DateTimeInput
        :model-value="f.state.value"
        :name="f.name"
        :disabled="field.disabled"
        :has-errors="invalid"
        :field-touched="f.state.meta.isTouched"
        @update:model-value="f.handleChange"
        @blur="f.handleBlur"
      />
    </template>
  </FormField>

  <component
    :is="form.Field"
    v-else-if="field.inputType === 'checkbox'"
    :name="field.fieldName"
  >
    <template #default="{ field: f }">
      <label
        class="group/field flex cursor-pointer items-start gap-2 px-3 py-2"
      >
        <Checkbox
          :model-value="!!f.state.value"
          :disabled="field.disabled"
          @update:model-value="
            (v) => {
              f.handleChange(v === true)
              f.handleBlur()
            }
          "
        />
        <span class="flex flex-col gap-1">
          <span
            class="text-xs font-medium leading-4 text-neutral-700 dark:text-neutral-200"
          >
            {{ field.label }}
          </span>
          <span
            v-if="field.hint"
            class="text-muted-foreground whitespace-pre-line text-xs leading-snug"
          >
            {{ field.hint }}
          </span>
        </span>
      </label>
    </template>
  </component>

  <FormField
    v-else-if="field.inputType === 'dropdown'"
    :form="form"
    :name="field.fieldName"
    :label="field.label"
    :required="requiredMessage"
    :disabled="field.disabled"
    :hint="field.hint ?? undefined"
  >
    <template #default="{ field: f, invalid }">
      <SelectInput
        :id="f.name"
        :model-value="f.state.value"
        :items="selectItems"
        :disabled="field.disabled"
        :clearable="!field.required"
        :aria-invalid="invalid"
        @update:model-value="f.handleChange"
        @blur="f.handleBlur"
      />
    </template>
  </FormField>

  <FormField
    v-else
    :form="form"
    :name="field.fieldName"
    :label="field.label"
    :required="requiredMessage"
    :disabled="field.disabled"
    :hint="field.hint ?? undefined"
  >
    <template #default="{ field: f }">
      <InputGroupInput
        :id="f.name"
        :model-value="f.state.value ?? ''"
        :disabled="field.disabled"
        @update:model-value="f.handleChange"
        @blur="f.handleBlur"
      />
    </template>
  </FormField>
</template>
